import { ActionType, WhiteLabelContentMode } from "generated/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as http from "util/http";
import { stableQueryOptions } from "util/requests";
import { brandingQueryClient } from "./branding-query-client";
import { useIsAllowed } from "./hooks";

export interface ProductBranding {
    logoUrl: string;
    faviconUrl: string;
    supportEmailAddress: string;
    productName: string;
    contentMode: WhiteLabelContentMode;
}

interface HookResponse {
    branding: ProductBranding | null;
    isLoading: boolean;
}

export const useProductBranding = (): HookResponse => {
    const fetchBranding = useCallback(async () => {
        const response = await http.get(http.newRequestDescription(), "/api/branding");
        return response.data as ProductBranding;
    }, []);

    const [data, setData] = useState<ProductBranding | undefined>();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            const existingData =
                brandingQueryClient.getQueryData<ProductBranding>("getBranding");
            if (existingData) {
                setData(existingData);
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const result = await brandingQueryClient.fetchQuery(
                    "getBranding",
                    fetchBranding,
                    stableQueryOptions(),
                );
                setData(result);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [fetchBranding]);

    const appBranding = useMemo(() => {
        return data || null;
    }, [data]);

    return {
        branding: appBranding,
        isLoading,
    };
};

export const useProductName = (): string => {
    const { branding } = useProductBranding();
    return branding?.productName || "";
};

export const useSupportEmailAddress = (): string => {
    const { branding } = useProductBranding();
    return branding?.supportEmailAddress || "";
};

export const useWhiteLabelContentMode = (): WhiteLabelContentMode => {
    const { branding, isLoading } = useProductBranding();
    const override = useIsAllowed(ActionType.ViewRestrictedWhiteLabelContent);

    if (isLoading) {
        return WhiteLabelContentMode.Restricted;
    }

    if (override) {
        return WhiteLabelContentMode.Full;
    }

    return branding?.contentMode || WhiteLabelContentMode.Restricted;
};
